import { atom, useSetAtom } from "jotai";
import { useCallback } from "react";
import { atomWithLocalStorage } from "common/storage";

import { DEFAULT_LEARNING_HUB_TAB, type LearningHubTabId } from "./constants";

/**
 * Whether the "How to use Sociability" modal is open, and on which tab.
 *
 * In memory: it is opened by a button press, not restored on reload.
 */
export const learningHubAtom = atom<{
  open: boolean;
  tab: LearningHubTabId;
  /** `entry_point` for `how_to_viewed`. */
  entryPoint: string;
}>({ open: false, tab: DEFAULT_LEARNING_HUB_TAB, entryPoint: "organic" });

/** Opens the modal, on `tab` or the default one. */
export function useOpenLearningHub() {
  const setHub = useSetAtom(learningHubAtom);
  return useCallback(
    (
      tab: LearningHubTabId = DEFAULT_LEARNING_HUB_TAB,
      entryPoint = "organic",
    ) => setHub({ open: true, tab, entryPoint }),
    [setHub],
  );
}

/** Signed-out readers' bucket — @see `viewerKey`. */
export const LEARNING_HUB_GUEST_VIEWER = "guest";

/** `lessonId -> true` for one viewer. */
export type LearningHubViewedLessons = Record<string, boolean>;

/**
 * Which lessons have been opened, bucketed by viewer, so a shared browser
 * shows each account its own circles. Same key and shape as native's MMKV
 * copy; local only, because having read a tutorial is nothing the server
 * records.
 */
export const learningHubViewedAtom = atomWithLocalStorage<
  Record<string, LearningHubViewedLessons>
>("learningHub.viewedLessons.v2", {});

/**
 * The bucket for a viewer, or `null` while `useAuth` has not answered yet —
 * "unknown" must not be filed under the guest bucket.
 */
export function viewerKey(
  ownerId: number | string | null | undefined,
  isResolved: boolean,
): string | null {
  if (ownerId !== null && ownerId !== undefined) return String(ownerId);
  return isResolved ? LEARNING_HUB_GUEST_VIEWER : null;
}
